import React, { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ShieldAlert, UploadCloud } from "lucide-react";
import { ALLOWED, LIMITS_TEXT, REMINDER, token } from "./ideaUtils";
import { toast } from "sonner";

export function CategorySelect({ cats, value, onChange, testId, allowAll }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm w-full" data-testid={testId}>
      <option value="">{allowAll ? "All categories" : "Choose a category…"}</option>
      {cats.filter((c) => allowAll || !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}{c.archived ? " (archived)" : ""}</option>)}
    </select>
  );
}

export function checkFiles(list) {
  const files = Array.from(list || []);
  if (files.length > 5) return "Choose up to 5 files";
  if (files.reduce((s, f) => s + f.size, 0) > 25 * 1024 * 1024) return "Files total more than 25 MB";
  const bad = files.find((f) => !ALLOWED.includes(f.name.split(".").pop().toLowerCase()) || f.size > 10 * 1024 * 1024);
  return bad ? `${bad.name}: unsupported type or over 10 MB` : null;
}

export async function uploadFiles({ org, category, description, files, onProgress, clientToken }) {
  const fd = new FormData();
  fd.append("organization_id", org); fd.append("category_id", category); fd.append("description", description || ""); fd.append("client_token", clientToken);
  files.forEach((f) => fd.append("files", f));
  const { data } = await api.post("/ideas/resources/upload", fd, { onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)) });
  return data;
}

export function ScanNotice({ configured }) {
  if (configured) return null;
  return <p className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 flex gap-2" data-testid="ideas-scan-notice"><ShieldAlert className="w-4 h-4 shrink-0" />Malware scanning isn't configured yet. Uploaded files are held in quarantine and won't appear in the shared library until scanning is enabled.</p>;
}

export function UploadDialog({ org, cats, scanner, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [cat, setCat] = useState("");
  const [desc, setDesc] = useState("");
  const [pct, setPct] = useState(null);
  const [results, setResults] = useState(null);
  const tok = useRef(token());
  const pick = (e) => { const err = checkFiles(e.target.files); if (err) { toast.error(err); e.target.value = ""; return; } setFiles(Array.from(e.target.files)); };
  const send = async () => {
    if (!cat) return toast.error("Choose a category");
    setPct(0);
    try { const d = await uploadFiles({ org, category: cat, description: desc, files, onProgress: setPct, clientToken: tok.current }); setResults(d.results); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setPct(null); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ideas-upload-dialog">
        <DialogHeader><DialogTitle>Upload a Resource</DialogTitle><DialogDescription>{LIMITS_TEXT}</DialogDescription></DialogHeader>
        <p className="text-xs text-slate-600 rounded-md bg-slate-50 px-3 py-2">{REMINDER}</p>
        <ScanNotice configured={scanner} />
        {results ? (
          <ul className="space-y-1 text-sm" data-testid="ideas-upload-results">{results.map((r) => (
            <li key={r.name} className={r.ok ? "text-emerald-700" : "text-rose-700"}>{r.name}: {r.ok ? (r.resource?.status === "available" ? "Shared" : "Held in quarantine pending malware scan") : r.error}</li>))}</ul>
        ) : (
          <>
            <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 p-6 cursor-pointer hover:border-[#006090]" data-testid="ideas-upload-drop">
              <UploadCloud className="w-6 h-6 text-[#006090]" /><span className="text-sm mt-1">{files.length ? files.map((f) => f.name).join(", ") : "Choose files"}</span>
              <input type="file" multiple className="sr-only" accept={ALLOWED.map((e) => `.${e}`).join(",")} onChange={pick} data-testid="ideas-upload-input" />
            </label>
            <CategorySelect cats={cats} value={cat} onChange={setCat} testId="ideas-upload-category" />
            <Textarea rows={2} placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="ideas-upload-desc" />
            {pct !== null && <div className="h-2 rounded bg-slate-100"><div className="h-2 rounded bg-[#006090] transition-[width]" style={{ width: `${pct}%` }} data-testid="ideas-upload-progress" /></div>}
          </>)}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>{results ? "Done" : "Cancel"}</Button>
          {!results && <Button onClick={send} disabled={!files.length || pct !== null} className="bg-brand hover:bg-brand-strong" data-testid="ideas-upload-submit">Upload</Button>}</div>
      </DialogContent>
    </Dialog>
  );
}

export function EditResourceDialog({ r, cats, onClose, onDone }) {
  const [f, setF] = useState({ display_name: r.display_name, description: r.description || "", category_id: r.category_id });
  const save = async () => { try { await api.patch(`/ideas/resources/${r.id}`, f); toast.success("Saved"); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ideas-edit-resource">
        <DialogHeader><DialogTitle>Edit resource</DialogTitle><DialogDescription>Original file: {r.original_filename}</DialogDescription></DialogHeader>
        <Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="ideas-edit-name" />
        <CategorySelect cats={cats} value={f.category_id} onChange={(v) => setF({ ...f, category_id: v })} testId="ideas-edit-category" />
        <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" data-testid="ideas-edit-desc" />
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-brand hover:bg-brand-strong" data-testid="ideas-edit-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
}
